import { AnimatePresence, motion } from 'framer-motion';
import { cn, NoddiIcon, NoddiIconName } from 'noddi-ui-common';
import { useId, useRef, useState } from 'react';
import { webButtonStyling } from '../../helpers/webButtonStyling';
import { useOnClickOutside } from '../../hooks/useOnClickOutside';
import { NoddiTooltip } from './NoddiTooltip';

export type NoddiDropdownOption<ValueType = string> = {
  label: string;
  value: ValueType;
  /** Before the label, in the menu and on the button while picked. */
  icon?: NoddiIconName;
  /** A muted line under the label in the menu: what picking the option means. */
  description?: string;
};

/**
 * A labelled bucket of options. Rendered as a section header above its own
 * options so the shared trait (profile type, department, …) is stated once
 * instead of repeated as a suffix on every row.
 */
export type NoddiDropdownOptionGroup<ValueType = string> = {
  label: string;
  icon?: NoddiIconName;
  options: NoddiDropdownOption<ValueType>[];
};

type NoddiDropdownCommonProps<ValueType> = {
  placeholder: string;
  selectedOption?: NoddiDropdownOption<ValueType> | null;
  onSelect: (option: NoddiDropdownOption<ValueType>) => void;
  startIcon?: NoddiIconName;
  className?: string;
  label?: string;
  tooltip?: string;
  wrapperClassName?: string;
  labelClassName?: string;
  menuClassName?: string;
};

// Flat list or buckets, never both — two sources of options would silently drop one.
type NoddiDropdownOptionsProps<ValueType> =
  | { options: NoddiDropdownOption<ValueType>[]; groups?: never }
  | { groups: NoddiDropdownOptionGroup<ValueType>[]; options?: never };

export type NoddiDropdownProps<ValueType = string> = NoddiDropdownCommonProps<ValueType> &
  NoddiDropdownOptionsProps<ValueType>;

export const NoddiDropdown = <ValueType,>({
  options,
  groups,
  placeholder,
  selectedOption,
  onSelect,
  startIcon,
  className,
  label,
  tooltip,
  wrapperClassName,
  labelClassName,
  menuClassName
}: NoddiDropdownProps<ValueType>) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const labelId = useId();
  const valueId = useId();
  const groupLabelId = useId();

  // A single headerless group keeps the flat `options` API rendering as before.
  const optionGroups: { label?: string; icon?: NoddiIconName; options: NoddiDropdownOption<ValueType>[] }[] =
    groups ?? [{ options: options ?? [] }];

  const toggleMenu = () => setIsOpen((prev) => !prev);
  const closeMenu = () => setIsOpen(false);

  const handleSelect = (option: NoddiDropdownOption<ValueType>) => {
    onSelect(option);
    closeMenu();
  };

  useOnClickOutside(dropdownRef, closeMenu);

  return (
    <div className={cn('flex flex-col gap-0.5', wrapperClassName)}>
      {label && (
        <div className='flex items-center gap-1'>
          <span id={labelId} className={cn('text-sm', labelClassName)}>
            {label}
          </span>
          {tooltip && <NoddiTooltip content={tooltip} />}
        </div>
      )}
      <div ref={dropdownRef} className={cn('relative inline-block text-left')}>
        <button
          type='button'
          onClick={toggleMenu}
          // Both ids: `aria-labelledby` replaces the name computed from content, so
          // naming the button by the label alone would drop the selected value a
          // screen reader announces.
          aria-labelledby={label ? `${labelId} ${valueId}` : undefined}
          className={cn(
            'inline-flex w-full items-center justify-center gap-2 border border-outlineStroke bg-white px-3 py-2 text-start text-black shadow-xs',
            webButtonStyling,
            'min-w-52',
            className
          )}
        >
          {(selectedOption?.icon ?? startIcon) && <NoddiIcon name={selectedOption?.icon ?? startIcon!} />}
          <span
            id={valueId}
            className={cn('flex-1 truncate md:text-sm', !selectedOption?.label && 'text-muted-foreground')}
          >
            {selectedOption?.label ?? placeholder}
          </span>
          <span className={cn('ml-auto transition-transform duration-300 ease-out', isOpen && 'rotate-180')}>
            <NoddiIcon name='ChevronDown' />
          </span>
        </button>

        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.2 }}
              className={cn(
                'absolute left-0 z-10 mt-1 max-h-40 min-w-full overflow-x-hidden overflow-y-auto rounded-xl bg-white whitespace-nowrap shadow-lg ring-1 ring-outlineStroke md:max-h-72',
                menuClassName
              )}
            >
              {optionGroups.map((group, groupIndex) => (
                <div
                  key={group.label ?? groupIndex}
                  role={group.label ? 'group' : undefined}
                  aria-labelledby={group.label ? `${groupLabelId}-${groupIndex}` : undefined}
                  // The divider belongs to the bucket below it, so the first one has none.
                  className={cn(groupIndex > 0 && 'border-t border-border')}
                >
                  {group.label && (
                    <div
                      id={`${groupLabelId}-${groupIndex}`}
                      className='flex items-center gap-1.5 px-4 pt-3 pb-1 text-xs font-semibold tracking-wider text-muted-foreground uppercase'
                    >
                      {group.icon && <NoddiIcon name={group.icon} size='xSmall' />}
                      {group.label}
                    </div>
                  )}
                  {group.options.map((option) => {
                    const isSelected = selectedOption?.value === option.value;

                    return (
                      <button
                        key={String(option.value)}
                        onClick={(e) => {
                          e.preventDefault();
                          handleSelect(option);
                        }}
                        className={cn(
                          'flex w-full items-center gap-2 rounded-none! px-4 py-2 text-start text-black hover:cursor-pointer hover:bg-grey/5',
                          isSelected && 'bg-card-light-background font-medium text-primary',
                          webButtonStyling
                        )}
                      >
                        {option.icon && (
                          <NoddiIcon
                            name={option.icon}
                            size='small'
                            // Beside the label, not the middle of label and description.
                            className={cn('shrink-0', option.description && 'mt-0.5 self-start')}
                          />
                        )}
                        {option.description ? (
                          <span className='flex min-w-0 flex-1 flex-col'>
                            <span className='truncate'>{option.label}</span>
                            <span className='text-xs font-normal whitespace-normal text-muted-foreground'>
                              {option.description}
                            </span>
                          </span>
                        ) : (
                          <span className='flex-1 truncate'>{option.label}</span>
                        )}
                        {isSelected && <NoddiIcon name='Check' size='small' className='shrink-0' />}
                      </button>
                    );
                  })}
                </div>
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};
